import type { SuggestionData } from '@ckeditor/ckeditor5-track-changes/src/trackchanges'
import { Plugin } from 'ckeditor5'
import { Users } from 'ckeditor5-premium-features'
import { Me } from './me'

/**
 * Plugin to add users to the editor (required for track changes integration - specifically for adding current user and highlighting formats).
 * @extends {Plugin} - Must be used with the Users plugin.
 */
export class UserPlugin extends Plugin {
    static requires: readonly ['Users']
    static pluginName = 'UserPlugin'

    init = () => {
        const usersPlugin = this.editor.plugins.get('Users') as Users
        if (!usersPlugin) {
            console.error('Users plugin not found')
            return
        }
        // Add the logged-in user as "me"
        this._defineMe(usersPlugin)
        // Add collaborators from initial suggestions in editor config.
        this._defineCollaborators(usersPlugin)
    }

    private _defineMe = (usersPlugin: Users) => {
        const loggedInUser = this.editor.config.get('me') as Me
        if (!loggedInUser?.id) {
            console.error('No valid logged-in user found')
            return
        }
        // Add the current user if not already added.
        if (!usersPlugin.getUser(loggedInUser.id)) {
            usersPlugin.addUser({
                id: loggedInUser.id,
                name: loggedInUser.name || 'Me',
            })
        }
        // Define the current user as "me".
        usersPlugin.defineMe(loggedInUser.id)
    }

    private _defineCollaborators = (usersPlugin: Users) => {
        const initialSuggestions = this.editor.config.get(
            'initialSuggestions'
        ) as SuggestionData[]

        /**
         * Define the anonymous user.
         * This is required for track changes integration to work with suggestions that doesn't have an authoredUserId.
         */
        usersPlugin.addUser({
            id: 'anonymous1',
            name: 'Anonymous',
        })

        // For each suggestion, add the collaborator if user data is available.
        initialSuggestions?.forEach(({ authorId }) => {
            const author = usersPlugin.getUser(authorId)
            if (author !== null) {
                usersPlugin.addUser(author)
            }
        })
    }
}
